import React, { useContext } from "react";
import { Suspense, useEffect, useRef, useState } from "react";
import "./styles.less";
import { Modal } from "shared-components/Components/Modal/web";
import { useKeywordLocation } from "./KeywordLocation.hook";
import { Input } from "antd";
import { LocationOutlinedIcon } from "shared-components/Components/CustomIcons/LocationOutlinedIcon/web";
import { SearchIcon } from "shared-components/Components/CustomIcons/SearchIcon/web";
import { useTheme } from "shared-components/Styles/ThemeHook";
import { useWindowSize } from "shared-components/GlobalHooks/useWindowSize";
import { CloseIcon } from "shared-components/Components/CustomIcons/CloseIcon/web";
import { firstLetterToUppercase, getOptionLabel } from "src/utils/Functions";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import { useRecentSearches } from "./useRecentSearches";
import { RecientLocations } from "./RecientsLocations";
import { PropertyData } from "src/contexts/PropertyData/PropertyDataProvider";
import HouseIcon from "shared-components/Components/CustomIcons/HouseIcon/web";
import { LocationData } from "src/Components/Filters/LocationSearch/types";

export const LocationTagHolder = ({
    changeFilters,
    filtersTags,
    show
}) => {
    if (!show) return null;

	const { translations, country_code } = useContext(ConfigStateContext);
    const {theme} = useTheme();
    const { 
        recentSearches,
        addRecentSearch,
    } = useRecentSearches();

    const screen = useWindowSize()
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [tags, setTags] = useState([]);
    const [locationOptions, setLocationOptions] = useState<LocationData[]>([]);
    const [resultByRef, setResultByRef] = useState<PropertyData | null>(null);

    const {
		keyword,
		onSearch,
		onSelect,
		searchResults,
        refResults,
		searchLoading,
	} = useKeywordLocation({
        className:null,
		searchByProject:false,
		searchByReference:true,
		locations:[],
        filtersTags,
        changeFilters
	});

    const removeNeighborhood = (location) => {
        const locationsSelected = Array.isArray(filtersTags?.locations) ? filtersTags?.locations[0]?.value : filtersTags?.locations?.value
        const newLocation = locationsSelected.filter(item => item.id !== location.id);

        changeFilters({
            locations: [
                {
                    text: "locations",
                    value: newLocation ?? [],
                },
            ],
            map_polygons: null
        });
    }

    const removeSearchstring = () => {
        changeFilters({
            searchstring: null,
        });
    }

    const openModal = () => {
        setModalOpen(true); 
        if(!screen.lg) document?.body?.classList.add('no-scroll'); 
    }

    const closeModal = () => {
        setModalOpen(false);
        document?.body?.classList.remove('no-scroll');
    }

    const handleClickOutside = (event) => {
        if(!screen.lg) {
            return false;
        }
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          closeModal()
        }
    };

    const handleEscape = (event) => {
        if (event.key === 'Escape') {
          closeModal()
        }
    };

    const handleSelectLocation = (option) => {
        setInputValue("");
        closeModal();
        addRecentSearch({type: 'location', ...option});
        onSelect(option.name, option);
    }

    const handleSelectKeyword = (value) => {
        addRecentSearch({type: 'keyword', name: value})
        onSelect(value, {__typename: 'keyword', name: value});
        closeModal();
    }

    useEffect(() => {
        let newTags = [];

        //Si locations no es un array, lo vuelvo un array
        if (filtersTags?.locations) {
            const locations = typeof filtersTags?.locations?.length === 'undefined' ? [filtersTags?.locations] : filtersTags?.locations;
    
            locations?.map((location) => {
                location.value.map((item) => {
                    newTags.push({
                        text: item.name,
                        value: item.id,
                        onRemove: () => {
                            removeNeighborhood(item);
                            onSelect("", null);
                        },
                    })
                })
            })
            
        }

        if(filtersTags?.searchstring) {
            newTags.push ({
                text: filtersTags?.searchstring?.text,
                value: filtersTags?.searchstring?.value,
                onRemove: () => {removeSearchstring()},
            })
        };

        setTags(newTags);
    }, [filtersTags?.locations, filtersTags?.searchstring]);

    useEffect(() => {
        if (searchResults?.searchLocation) setLocationOptions(searchResults?.searchLocation)
        else setLocationOptions([]);
        if (refResults) setResultByRef(refResults);
        else setResultByRef(null);
    }, [searchResults?.searchLocation, refResults]);

    useEffect(() => {
        if (!keyword.length) setLocationOptions([]);
    }, [keyword])
    
    useEffect(() => {
        document?.addEventListener('mousedown', handleClickOutside);
        document?.addEventListener('keydown', handleEscape);
        return () => {
            document?.removeEventListener('mousedown', handleClickOutside);
            document?.removeEventListener('keydown', handleEscape);
        };
    }, [screen?.lg]);

    useEffect(() => {
        if (modalOpen) {
            setInputValue("");
            setLocationOptions([]);
            setResultByRef(null);
        };
    }, [modalOpen]);

    const handleSelectReference = (property: PropertyData) => {
        addRecentSearch({
            type: 'reference',
            id: property.id,
            code: property.code,
            title: property.title,
            property_type: {
            name: property.property_type.name,
            id: property.property_type.id,
            },
            link: property.link,
        });
        
        if (property.link) {
            window.location.href = property.link;
        }
    };

    const ResultsWithRef = () => {
        if (!resultByRef) return null;

        return (
            <li
            className="option"
            onClick={() => handleSelectReference(resultByRef)}
            >
            <span className="location-icon">
                <HouseIcon />
            </span>
            <span className="location-text">
                <span className="text">{resultByRef.title}</span>
                <span className="group">{resultByRef.property_type.name}</span>
            </span>
            </li>
        );
    };
    
    const DropdownElements = ()=> {
        if (!searchLoading && locationOptions.length === 0 && resultByRef===null && inputValue!==""){
            return(
                <div
                    className="option"
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderBottom: "solid 1px #dadada",
                        padding: "10px 5px",
                    }}
                    onClick={() => handleSelectKeyword(inputValue)}>
                    <div style={{ position: "relative", top: 2 }}>
                        <SearchIcon width={20} />
                    </div>
                    <div
                        style={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            paddingLeft: 10,
                            lineHeight: 1.1,
                        }}>
                        <span>
                            Buscar inmuebles relacionados a{" "}
                            <b style={{ fontWeight: "bold" }}>{keyword}</b>
                        </span>
                    </div>
                </div>
            )
        }
        else if(searchLoading && !locationOptions.length && resultByRef === null) {
            return (
                <span style={{ display: "flex", gap: 10, padding: 15 }}>
                    <span>
                        <Suspense fallback={<div>...</div>}>
                            {React.createElement(
                                React.lazy(
                                    async () =>
                                        await import("@ant-design/icons/LoadingOutlined")
                                )
                            )}
                        </Suspense>
                    </span>
                    Cargando
                </span>
            )
        }
        else if(!!recentSearches.length && !inputValue.length){
            return (
                <RecientLocations
                    recientLocations={recentSearches}
                    handleSelectKeyword={handleSelectKeyword}
                    handleSelectLocation={handleSelectLocation}
                    handleSelectReference={handleSelectReference}
                />
            )
        }
        else if(!searchLoading && inputValue !== "") {
            return (
                <div className={"option-list " + (screen.lg ? "" : "mobile")}>
                    {country_code !== "CO" && resultByRef && <ResultsWithRef />}
                    {locationOptions.map((option, i) => {
                            const label = getOptionLabel(option);
                            const group = translations[option.type.toLowerCase()];

                            return (
                                <div
                                    className="option"
                                    key={i}
                                    onClick={() => handleSelectLocation(option)}>
                                    <span className="location-icon">
                                        <LocationOutlinedIcon width={20} />
                                    </span>

                                    <span className="location-text">
                                        <span
                                            className="text"
                                            dangerouslySetInnerHTML={{
                                                __html: label,
                                            }}></span>
                                        <span className="group">{group}</span>
                                    </span>
                                </div>
                            );
                        })
                    }
                </div>
            )
        }
        return <></>
    }

    const modalContent = (
		<div className={"location-main-wrapper " + (screen.lg ? "desktop" : "mobile")}>
			<div className="holder">
				{tags.length > 0 && (
					<div
						className="tag-holder"
						style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
						{tags.map(e => (
							<div
								key={e.value}
								className="filter-tag"
								onClick={() => {
									e.onRemove();
									inputRef?.current?.focus();
								}}>
								{e.text}
								<span className="close">
									<CloseIcon width={12} />
								</span>
							</div>
						))}
					</div>
				)}

				<div className="holder-content">
					<Input
						className={tags.length > 0 ? "" : "notags"}
						ref={inputRef}
						autoFocus={true}
						onChange={e => {
                            const { value } = e.target;
                            const valueCleaned = value.replace(/[^\w\sáéíóúÁÉÍÓÚñÑ˜´]/g, '');

							onSearch(valueCleaned);
							setInputValue(valueCleaned);
						}}
						placeholder={`${firstLetterToUppercase(
							translations.busca
						)} por ubicación o palabra clave`}
						allowClear={true}
						onFocus={e => {
							setInputValue(e.target.value);
						}}
						value={inputValue}
					/>
                    <DropdownElements />
					
					{!screen.lg && (
						<div
							className="apply-button"
							style={{
								borderRadius: 10,
								padding: 10,
								backgroundColor: theme.colors.primaryColor,
								color: "white",
								position: "absolute",
								bottom: 10,
								right: 10,
								left: 10,
							}}
							onClick={() => {
								closeModal();
							}}>
							<span>Aplicar</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);

    return (
        <div ref={dropdownRef} style={{position: 'relative'}}>
            {(screen.lg && modalOpen) ? modalContent : null}

            {!modalOpen && <div className="autocompletTagHolder" 
            onClick={() => { openModal()}}
            >
                {tags.length > 0 && 
                    <div className="filter-tag" style={{maxWidth: '70%', cursor: 'pointer'}}>
                        <span style={{
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap'
                            
                        }}>{tags[0].text}</span>
                        <span className="close" onClick={(e) => {e.stopPropagation();tags[0].onRemove()}}>
                            <CloseIcon width={12} />
                        </span>
                    </div>
                }
                {tags.length > 1 && 
                    <div className="filter-tag">
                        {tags.length - 1} más
                    </div>
                }

                <span
                    className="autocomplete-placeholder"
                >
                    {`${firstLetterToUppercase(translations.busca)} por ubicación o palabra clave`}
                </span>
            </div>}

            {!screen.lg && <Modal show={modalOpen} onClose={() => { closeModal() }} classes="location-modal" backdropStyles={{padding: 0}}>
               {modalContent}
            </Modal>}
        </div>
    );
}